import React, { useContext, useMemo } from "react";
import DiscussionMyMessage from "./DiscussionMyMessage";
import DiscussionOtherMessage from "./DiscussionOtherMessage";
import BookingConfirmationCard from "./BookingConfirmationCard";
import fileDownload from "js-file-download";
import Image from "components/File/Image";
import { fetchFile } from "hooks/file";
import { AuthContext } from "context";
import moment from "moment";
import ScrollToBottom from "./ScrollToBottom";
import type { RenderItem, MessageUser } from "../types";

interface Props {
    checkIdentityOfUser: MessageUser | undefined;
    renderItems: RenderItem[];
}

const DiscussionContent: React.FC<Props> = ({ checkIdentityOfUser, renderItems }) => {
    const auth = useContext(AuthContext);

    const renderImageOtherPerson = useMemo(() => {
        if (!checkIdentityOfUser) {
            return null;
        }

        if (checkIdentityOfUser.picture_id) {
            return <Image id={checkIdentityOfUser.picture_id} className="w-full h-full object-cover" />;
        }

        return (
            <span className="text-xs font-semibold">
                {checkIdentityOfUser.first_name.charAt(0)}
                {checkIdentityOfUser.last_name.charAt(0)}
            </span>
        );
    }, [checkIdentityOfUser]);

    const downloadFile = (event: React.MouseEvent, id: string | undefined) => {
        if (!id) return;
        event.preventDefault();
        fetchFile(id).then((response) => {
            fileDownload(response.content, response.name);
        });
    };

    // Index of the last message sent by current user — only this one shows the read indicator
    const lastMyMessageIndex = renderItems.reduce<number>(
        (last, item, i) => (item.kind === "message" && auth.account?.user.id === item.data.user_id ? i : last),
        -1,
    );

    return (
        <div className="py-4 flex-1 overflow-y-auto min-h-0">
            {renderItems.map((item, index) => {
                if (item.kind === "offer_card") {
                    return (
                        <BookingConfirmationCard
                            key={`offer-card-${item.data.created_at}`}
                            application={item.data}
                            isEmployerView={item.isEmployerView}
                            onAccept={item.onAccept}
                            onRefuse={item.onRefuse}
                        />
                    );
                }

                const { data: messageReceived } = item;
                const myMessage = auth.account?.user.id === messageReceived.user_id;
                const dateTime = moment(messageReceived.created_at).format("HH:mm");
                const isModerateMessage =
                    messageReceived.is_moderate_message === true ||
                    messageReceived.is_moderate_message === "1" ||
                    messageReceived.is_moderate_message === "true";

                const messageOrFile = (() => {
                    if (messageReceived.file) {
                        const fileName = messageReceived.file.real_name ?? messageReceived.file.name ?? "";
                        return (
                            <button
                                className="flex items-center gap-2 text-sm underline underline-offset-2"
                                onClick={(event) => downloadFile(event, messageReceived.file_id)}
                            >
                                <span className="icon icon-download"></span>
                                <span>{fileName}</span>
                            </button>
                        );
                    }
                    return <span className="whitespace-pre-line">{messageReceived.message}</span>;
                })();

                if (myMessage) {
                    const isLastMyMessage = index === lastMyMessageIndex;
                    const isRead = isLastMyMessage && messageReceived.is_read === true;
                    return (
                        <DiscussionMyMessage
                            key={index}
                            messageOrFile={messageOrFile}
                            dateTime={dateTime}
                            isModerateMessage={isModerateMessage}
                            isRead={isRead}
                            showReadIndicator={isLastMyMessage}
                        />
                    );
                }

                return (
                    <DiscussionOtherMessage
                        key={index}
                        renderImageOtherPerson={renderImageOtherPerson}
                        messageOrFile={messageOrFile}
                        dateTime={dateTime}
                    />
                );
            })}
            <ScrollToBottom />
        </div>
    );
};

export default DiscussionContent;
